import { useEffect } from 'react';
import { NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';

import { BlobHandlerErrorPanel } from '../components/BlobHandlerErrorPanel';
import { CrossFieldFlagsTable } from '../components/CrossFieldFlagsTable';
import { FiaSummary } from '../components/FiaSummary';
import { HitlQueue } from '../components/HitlQueue';
import { LogsPanel } from '../components/LogsPanel';
import { ManifestPreview } from '../components/ManifestPreview';
import { MetricsPanel } from '../components/MetricsPanel';
import { OutputPanel } from '../components/OutputPanel';
import { SchemaDriftBanner } from '../components/SchemaDriftBanner';
import { SchemaReview } from '../components/SchemaReview';
import { Spinner } from '../components/Spinner';
import { useRun } from '../hooks/useRun';
import { useRunEvents } from '../hooks/useRunEvents';
import { api } from '../services/api';
import { isBlobHandlerError } from '../services/blobHandlerError';
import type {
  ColumnPolicyOverride,
  HitlActionRequest,
  HitlBatchConfirmRequest,
  Manifest,
  RasSchemaResponse,
  RunStatus,
} from '../services/types';
import { useAppStore } from '../store/appStore';

/**
 * Per-run reviewer view — tabs for FIA, schema, manifest, HITL.
 *
 * Data flow
 * ---------
 * - `useRun` manages per-artifact fetch state + caches into the Zustand store.
 * - `useRunEvents` subscribes to the WS stream and mirrors progress/status
 *   into the same store; the banner strip reflects it live.
 * - Drafts live in the store keyed by runId, so tab-switching and browser
 *   refresh don't discard unsaved edits.
 */
export function RunDetailPage() {
  const { runId = '' } = useParams();

  if (!runId) {
    return <Navigate to="/" replace />;
  }

  return <RunDetail runId={runId} />;
}

function RunDetail({ runId }: { runId: string }) {
  const setCurrentRunId = useAppStore((s) => s.setCurrentRunId);
  const drafts = useAppStore((s) => s.drafts[runId] ?? {});
  const setDraft = useAppStore((s) => s.setDraft);
  const clearDraft = useAppStore((s) => s.clearDraft);

  // useRun auto-fetches the FIA on mount; the other three fetches are
  // triggered lazily when their tabs are first opened.
  const { run, loading, errors, errorStatuses, fetchSchema, fetchManifest, fetchHits, retryFia } =
    useRun(runId);
  // Capture the ring-buffered events + connection state for the Logs tab.
  // useRunEvents is already subscribed to /ws/runs/:id/events regardless
  // of whether the Logs tab is open — opening it just makes them visible.
  const { events: runEvents, connected: runEventsConnected } = useRunEvents(runId);

  useEffect(() => {
    setCurrentRunId(runId);
    return () => setCurrentRunId(null);
  }, [runId, setCurrentRunId]);

  const statusTone = statusStyles[run.status ?? 'queued'];

  const onSaveSchema = async (next: RasSchemaResponse) => {
    // Placeholder: the mock server doesn't round-trip schema edits, but the
    // real backend will. Clear the draft on success.
    void next;
    clearDraft(runId, 'schema');
  };

  // v0.5 Stage E.4 — per-column threshold overrides save flow.
  // Dispatches to the PATCH /runs/:run_id/column_policies endpoint
  // and refreshes the run summary so the SchemaReview panel re-
  // hydrates from the latest carry-forward state.
  const onSaveColumnPolicies = async (
    overrides: Record<string, ColumnPolicyOverride>,
  ) => {
    await api.updateColumnPolicies(runId, {
      overrides,
      reviewer_id: 'reviewer',
    });
    // Refresh hits + run summary so the schema panel sees the
    // updated overrides on next render and the queue picks up any
    // new HITLs the re-kicked pipeline emits.
    await fetchHits();
  };

  const onSaveManifest = async (next: Manifest) => {
    await api.saveManifest(runId, next);
    clearDraft(runId, 'manifest');
    await fetchManifest();
  };

  const onRerun = async () => {
    await api.rerun(runId, { full_run: true, reviewer_id: 'reviewer' });
    await fetchHits();
  };

  const onHitAction = async (hitId: string, action: HitlActionRequest) => {
    await api.postHitAction(hitId, action);
    await fetchHits();
  };

  // v0.6 Stage F.4 — bulk-ack post + refresh. Mirrors ``onHitAction``'s
  // post-then-refresh shape; the explicit ``fetchHits()`` is what
  // distinguishes this from the v0.5 inline call in HitlQueue, which
  // skipped the refresh and left the sweeper visibly stuck on the old
  // recommended count after dispatch (caught by the Playwright sweeper
  // poll). See ``HitlQueue.tsx::handleConfirmAll`` for the fallback
  // path used by legacy unit-test mounts.
  const onHitBatchConfirm = async (
    request: HitlBatchConfirmRequest & { run_id: string },
  ) => {
    const { run_id, ...body } = request;
    await api.postBatchConfirm(run_id, body);
    await fetchHits();
  };

  return (
    <section aria-labelledby="run-title" className="space-y-6">
      {/* v0.4 Stage E.4 — Blob Handler error panel. Replaces the
          generic error block when the run failed because of a non-
          tabular upload (image / audio / unknown / text-only PDF /
          PDF safety-cap rejection). The panel decodes the
          ``error_reason`` into reviewer-friendly copy + a clear
          "upload a new file" CTA. */}
      {run.status === 'error' && isBlobHandlerError(run.error_reason) && (
        <BlobHandlerErrorPanel errorReason={run.error_reason!} />
      )}

      <header className="flex flex-wrap items-center gap-4 justify-between">
        <div>
          <h1 id="run-title" className="mb-1 text-2xl">
            Run <code className="text-xl font-mono">{runId}</code>
          </h1>
          <div className="flex items-center gap-3 text-sm flex-wrap">
            <span className={`text-xs uppercase font-semibold rounded-sm px-2 py-0.5 ${statusTone}`}>
              {run.status ?? 'queued'}
            </span>
            {/*
             * Partial-run warning: appears when the backend completed
             * the pipeline but MOA.execute reported run_result.status =
             * "partial" (one or more cleansers failed). Without this
             * badge, a reviewer sees "complete" (green) with no
             * indication some columns are silently un-cleansed.
             */}
            {run.run_result_status === 'partial' && (
              <span
                className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-warn/15 text-warn"
                title={
                  run.anomaly_count
                    ? `${run.anomaly_count} per-cell anomaly/anomalies recorded. See Manifest tab for detail.`
                    : 'One or more cleansers failed during execution.'
                }
              >
                partial cleanse
                {run.anomaly_count ? ` · ${run.anomaly_count} flag${run.anomaly_count === 1 ? '' : 's'}` : ''}
              </span>
            )}
            {typeof run.progressPct === 'number' && (
              <span className="text-muted">progress: {run.progressPct}%</span>
            )}
            {run.hits && run.hits.length > 0 && (
              <span className="text-warn">
                {run.hits.filter((h) => h.status === 'open').length} open HITL
              </span>
            )}
          </div>
        </div>
      </header>

      <nav aria-label="Run sections" className="flex gap-2 flex-wrap border-b border-border">
        <TabLink to="fia" label="Format (FIA)" />
        <TabLink to="schema" label="Schema (RAS)" />
        <TabLink to="manifest" label="Manifest (MOA)" />
        {/*
         * HITL tab badge shows *open* count only, matching the header's
         * "N open HITL" indicator. Resolved / escalated items are
         * history — visible inside the tab but not surfaced in the
         * badge. Omit the parenthetical entirely when there are no
         * open items (or no hits at all) so the tab reads cleanly.
         */}
        <TabLink
          to="hitl"
          label={(() => {
            const openCount = (run.hits ?? []).filter(
              (h) => h.status === 'open',
            ).length;
            return openCount > 0 ? `HITL (${openCount})` : 'HITL';
          })()}
        />
        <TabLink to="logs" label="Logs" />
        <TabLink to="metrics" label="Metrics" />
        <TabLink to="output" label="Output" />
        {/*
         * v0.4 Stage E.6 — Rules sub-tab. Shown only when the run has
         * cross-field flags persisted (i.e. confirmed rules applied).
         * The badge surfaces the violation count when non-zero;
         * "0 violations" runs still show the tab so reviewers can see
         * the rules-in-force confirmation, but with no count badge.
         */}
        {typeof run.cross_field_flag_count === 'number' && (
          <TabLink
            to="rules"
            label={
              run.cross_field_flag_count > 0
                ? `Rules (${run.cross_field_flag_count})`
                : 'Rules'
            }
          />
        )}
      </nav>

      <Routes>
        <Route index element={<Navigate to="fia" replace />} />
        <Route
          path="fia"
          element={
            <TabPanel
              loading={loading.fia && !run.fia}
              error={errors.fia}
              onRetry={retryFia}
              content={
                run.fia ? (
                  <FiaSummary
                    fia={run.fia}
                    highlight={highlightFromRun(run.schema, run.header_pii_columns)}
                    onRequestReveal={makeFiaRevealHandler(
                      runId,
                      highlightFromRun(run.schema, run.header_pii_columns)?.pii ?? [],
                    )}
                  />
                ) : null
              }
            />
          }
        />
        <Route
          path="schema"
          element={
            <TabPanel
              onEnter={() => !run.schema && !loading.schema && !errors.schema && fetchSchema()}
              loading={loading.schema && !run.schema}
              error={errors.schema}
              content={
                run.schema ? (
                  // Drift banner sits above the schema editor so the
                  // reviewer sees "your schema changed since last run"
                  // before they start interpreting the content below.
                  // Banner self-hides when drift is none / absent.
                  <div className="space-y-4">
                    <SchemaDriftBanner drift={run.schema_drift} />
                    <SchemaReview
                      schema={run.schema}
                      draft={drafts.schema}
                      onDraftChange={(next) => setDraft(runId, 'schema', next)}
                      onSave={onSaveSchema}
                      onDiscard={() => clearDraft(runId, 'schema')}
                      initialColumnPolicies={
                        run.column_policy_overrides ?? undefined
                      }
                      onSaveColumnPolicies={onSaveColumnPolicies}
                    />
                  </div>
                ) : null
              }
            />
          }
        />
        <Route
          path="manifest"
          element={
            <TabPanel
              onEnter={() => !run.manifest && !loading.manifest && !errors.manifest && fetchManifest()}
              loading={loading.manifest && !run.manifest}
              // 404 on GET /runs/:id/manifest is the backend's way of saying
              // "the pipeline halted before MOA planned anything." That's a
              // normal awaiting-review state, not an error — render it as a
              // soft empty-state card and suppress the red error block.
              error={errorStatuses.manifest === 404 ? undefined : errors.manifest}
              content={
                run.manifest ? (
                  <ManifestPreview
                    manifest={run.manifest}
                    draft={drafts.manifest}
                    onDraftChange={(next) => setDraft(runId, 'manifest', next)}
                    onSave={onSaveManifest}
                    onDiscard={() => clearDraft(runId, 'manifest')}
                    onRerun={onRerun}
                  />
                ) : errorStatuses.manifest === 404 ? (
                  <ManifestPendingCard
                    status={run.status}
                    // Count only *open* hits so the copy tracks reality. A
                    // resolved/escalated hit isn't "waiting on the reviewer."
                    openHitsCount={(run.hits ?? []).filter((h) => h.status === 'open').length}
                    errorReason={run.error_reason}
                    backendMessage={errors.manifest}
                  />
                ) : null
              }
            />
          }
        />
        <Route
          path="hitl"
          element={
            <TabPanel
              onEnter={() => !run.hits && !loading.hits && !errors.hits && fetchHits()}
              loading={loading.hits && !run.hits}
              error={errors.hits}
              content={
                run.hits ? (
                  <HitlQueue
                    hits={run.hits}
                    onAction={onHitAction}
                    onBatchConfirm={onHitBatchConfirm}
                    reviewerId="reviewer"
                  />
                ) : null
              }
            />
          }
        />
        <Route
          path="logs"
          element={
            // LogsPanel reads from useRunEvents' cached event buffer — no
            // separate fetch needed, no loading/error state. The panel
            // renders its own empty state when no events have arrived yet.
            <LogsPanel events={runEvents} connected={runEventsConnected} />
          }
        />
        <Route
          path="metrics"
          element={
            // MetricsPanel aggregates the same event buffer + the hits
            // we already fetched + the run summary into a per-run
            // snapshot. No extra network traffic.
            <MetricsPanel
              events={runEvents}
              hits={run.hits}
              run={{
                status: run.status,
                progressPct: run.progressPct,
                run_result_status: run.run_result_status,
                anomaly_count: run.anomaly_count,
                schema_drift: run.schema_drift,
              }}
            />
          }
        />
        <Route
          path="output"
          element={
            <OutputPanel
              runId={runId}
              runStatus={run.status}
              piiColumns={run.schema?.pii_columns ?? []}
            />
          }
        />
        {/*
         * v0.4 Stage E.6 — Rules sub-tab content. Renders a paginated
         * table of cross-field violation flags fetched via
         * GET /runs/:run_id/cross_field_flags. Mounted only when the
         * run carries a cross_field_flag_count (i.e. confirmed rules
         * applied to this run); the TabLink above mirrors the same
         * visibility rule.
         */}
        {typeof run.cross_field_flag_count === 'number' && (
          <Route
            path="rules"
            element={
              <CrossFieldFlagsTable
                runId={runId}
                runStatus={run.status}
                columns={run.schema?.column_inference ?? []}
                reviewerId="reviewer"
                onRuleCreated={() => void fetchHits()}
              />
            }
          />
        )}
      </Routes>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Local helpers
// ----------------------------------------------------------------------------

const statusStyles: Record<RunStatus, string> = {
  queued: 'bg-surface-alt text-muted',
  running: 'bg-brand/15 text-brand-dark',
  awaiting_review: 'bg-warn/15 text-warn',
  complete: 'bg-brand/15 text-brand-dark',
  error: 'bg-danger/15 text-danger',
};

function TabLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
          isActive ? 'border-brand-dark text-ink' : 'border-transparent text-muted hover:text-ink'
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function TabPanel({
  onEnter,
  loading,
  error,
  onRetry,
  content,
}: {
  onEnter?: () => void;
  loading: boolean;
  error?: string;
  onRetry?: () => void;
  content: React.ReactNode;
}) {
  useEffect(() => {
    onEnter?.();
    // We intentionally fire onEnter only on mount (entering the tab).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-muted">
        <Spinner /> Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="space-y-3">
        <p className="text-danger text-sm">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="text-sm underline text-brand-dark hover:text-brand"
          >
            Retry
          </button>
        )}
      </div>
    );
  }
  return <>{content}</>;
}

function highlightFromRun(
  schema?: { pii_columns?: string[]; ambiguous_columns?: string[] },
  headerPiiColumns?: string[] | null,
) {
  // v0.6 Stage B6 — fold in the post-FIA header-name scan so the
  // upload preview blurs PII-suspect columns the moment the reviewer
  // first sees the data (before RAS / discover populate
  // ``schema.pii_columns``). The two sources are unioned: the header
  // scan runs at FIA-time on column names only; the RAS schema adds
  // content-based detections later. Once both populate, the union
  // covers both classes of detection.
  const pii = Array.from(
    new Set([
      ...(schema?.pii_columns ?? []),
      ...(headerPiiColumns ?? []),
    ]),
  );
  if (pii.length === 0 && !schema?.ambiguous_columns?.length) {
    return undefined;
  }
  return {
    pii: pii.length > 0 ? pii : undefined,
    ambiguous: schema?.ambiguous_columns,
  };
}

/**
 * v0.6 Stage A.3 — build the ``onRequestReveal`` callback for the
 * Schema/FIA-tab preview. Mirrors the OutputPanel preview-tab wire-up
 * but tags the audit with ``surface="fia"`` so the run audit timeline
 * disambiguates which preview the reviewer revealed on.
 *
 * Audit-or-bust: if the POST rejects (network blip, transient DB
 * outage), the reveal is blocked and the reviewer can retry. The cells
 * never unblur without a corresponding audit row.
 */
function makeFiaRevealHandler(
  runId: string,
  piiColumns: string[],
): () => Promise<boolean> {
  return async () => {
    const ok = window.confirm(
      'Reveal PII values in this preview? This action will be recorded in ' +
        'the run audit log.',
    );
    if (!ok) return false;
    try {
      await api.recordPiiReveal(runId, 'fia', piiColumns);
      return true;
    } catch (err) {
      console.error('PII reveal audit failed; reveal blocked', err);
      return false;
    }
  };
}

/**
 * Soft empty-state for the Manifest tab when the backend returns 404
 * ("manifest not yet available"). Three sub-states:
 *
 * 1. `awaiting_review` with open hits — the pipeline halted on a gate and
 *    the reviewer needs to work the HITL queue. Copy points them there.
 * 2. `error` with a backend reason — the pipeline crashed (typically in
 *    MOA / a cleanser). Surface the reason so the user can see *why*.
 * 3. Anything else — terse "MOA hasn't produced a manifest" fallback.
 */
function ManifestPendingCard({
  status,
  openHitsCount,
  errorReason,
  backendMessage,
}: {
  status?: RunStatus;
  openHitsCount?: number;
  errorReason?: string | null;
  backendMessage?: string;
}) {
  const isError = status === 'error';
  const isAwaiting = status === 'awaiting_review';
  const hasOpenHits = typeof openHitsCount === 'number' && openHitsCount > 0;

  return (
    <div
      role="status"
      className={`rounded-md border p-5 space-y-2 text-sm ${
        isError
          ? 'border-danger/30 bg-danger/5'
          : 'border-border bg-muted/10'
      }`}
    >
      <p className="font-medium text-fg">
        {isError ? 'Pipeline error — manifest not produced' : 'Manifest not yet generated'}
      </p>
      <p className="text-muted">
        {isError
          ? 'The pipeline crashed before MOA could write a manifest. The reason is below — this is usually an agent or cleanser failing on an edge case in the data.'
          : isAwaiting && hasOpenHits
            ? `This run is awaiting review on an earlier stage of the pipeline. There ${openHitsCount === 1 ? 'is' : 'are'} `
            : isAwaiting
              ? 'This run is awaiting review, but every surfaced HITL item has been resolved. The pipeline should auto-resume shortly — if it does not, try a manual re-run.'
              : 'MOA has not yet produced a cleaning manifest for this run.'}
        {isAwaiting && hasOpenHits ? (
          <>
            <strong>{openHitsCount}</strong> item{openHitsCount === 1 ? '' : 's'} on the{' '}
            <strong>HITL</strong> tab — resolve {openHitsCount === 1 ? 'it' : 'them'} to unblock the pipeline.
          </>
        ) : null}
      </p>
      {isError && errorReason ? (
        <pre className="mt-2 max-h-48 overflow-auto rounded bg-surface-alt/40 p-3 text-xs text-fg whitespace-pre-wrap break-all">
          {errorReason}
        </pre>
      ) : null}
      {backendMessage && !isError ? (
        <p className="text-muted text-xs italic">Backend: {backendMessage}</p>
      ) : null}
    </div>
  );
}
